import React, { createContext, useContext, useState } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import { useAudio } from '@utils/providers/audio-context.tsx';
import { useCaption } from '@utils/providers/caption-context.tsx';
import { useNumpadShortcutsDisabled } from '@utils/hooks/use-numpad-shortcuts-disabled.ts';
import { useCaptionSegmentHotkeys } from '@utils/hooks/use-caption-segment-hotkeys.ts';

interface CaptionHotkeyContextType {
  shortcutsModalOpen: boolean;
  openShortcutsModal: () => void;
  closeShortcutsModal: () => void;
  searchOpen: boolean;
  openSearch: () => void;
  closeSearch: () => void;
}

const CaptionHotkeyContext = createContext<
  CaptionHotkeyContextType | undefined
>(undefined);

export function useCaptionHotkeys() {
  const ctx = useContext(CaptionHotkeyContext);
  if (!ctx)
    throw new Error(
      'useCaptionHotkeys must be used within CaptionHotkeyProvider'
    );
  return ctx;
}

interface CaptionHotkeyProviderProps {
  children: React.ReactNode;
}

export function CaptionHotkeyProvider({
  children
}: CaptionHotkeyProviderProps) {
  const {
    saveDocument,
    clickedIndex,
    addListItem,
    splitListItem,
    removeListItem,
    setClickedIndex,
    document: captionDocument,
    changeSegmentTimeForCurrentIndex
  } = useCaption();
  const {
    play,
    pause,
    isPlaying,
    rewind,
    forward,
    setTime,
    toggleLinkedPlayback
  } = useAudio();

  const numpadDisabled = useNumpadShortcutsDisabled();

  // Ctrl/Cmd+Z and Ctrl/Cmd+Y are handled in caption-main (outside a segment)
  // and in the segment editor itself; binding them here as well undid twice.

  // space or numpad 5 play pause. `numpad5` also matches the top-row 5, so
  // check the physical key.
  useHotkeys(['space', 'numpad5'], event => {
    if (event.key !== ' ' && (numpadDisabled || event.code !== 'Numpad5')) {
      return;
    }
    if (isPlaying) {
      pause();
    } else {
      play();
    }
  });

  // F5 Play/Pause - works while editing (contentEditable)
  useHotkeys(
    ['f5'],
    event => {
      event.preventDefault();
      if (isPlaying) {
        pause();
      } else {
        play();
      }
    },
    { enableOnFormTags: true, enableOnContentEditable: true }
  );

  // F7 Rewind 5 seconds - works while editing (contentEditable)
  useHotkeys(
    ['f7'],
    event => {
      event.preventDefault();
      rewind(5);
    },
    { enableOnFormTags: true, enableOnContentEditable: true }
  );

  // F8 Forward 5 seconds - works while editing (contentEditable)
  useHotkeys(
    ['f8'],
    event => {
      event.preventDefault();
      forward(5);
    },
    { enableOnFormTags: true, enableOnContentEditable: true }
  );

  // option or ctrl + arrow left rewind 5 seconds
  useHotkeys(['left', 'ctrl+left', 'meta+left'], () => {
    rewind(5);
  });

  // option or ctrl + right arrow forward 5 seconds
  useHotkeys(['right', 'ctrl+right', 'meta+right'], () => {
    forward(5);
  });

  // ctrl+s save document
  useHotkeys(['ctrl+s', 'meta+s'], event => {
    event.preventDefault();
    saveDocument(true);
  });

  // Playback-rate keyboard shortcuts have been removed. They previously bound
  // ctrl+plus / ctrl+= / ctrl+minus (and meta+…) and called preventDefault(),
  // which blocked the browser's native zoom (Utrecht #2XXX — Cladder/Stoffels
  // couldn't Ctrl+- to zoom out the page, it changed the playback speed
  // instead). Users can adjust playback rate via the dropdown in the media
  // controls.

  // ctrl+f opens the Find & Replace popover (#2233).
  const [searchOpen, setSearchOpen] = useState(false);
  useHotkeys(
    ['ctrl+f', 'meta+f'],
    event => {
      event.preventDefault();
      setSearchOpen(true);
    },
    { enableOnFormTags: true, enableOnContentEditable: true }
  );

  // ctrl+l toggle linked playback
  useHotkeys(
    ['ctrl+l', 'meta+l'],
    event => {
      event.preventDefault();
      toggleLinkedPlayback();
    },
    { enableOnFormTags: true, enableOnContentEditable: true }
  );

  useCaptionSegmentHotkeys({
    document: captionDocument,
    clickedIndex,
    setClickedIndex,
    addListItem,
    splitListItem,
    removeListItem,
    changeSegmentTimeForCurrentIndex,
    setTime,
    numpadDisabled
  });

  // Discoverable keyboard-shortcut overlay (#1920). Open with ? (Shift + /)
  // anywhere in the editor; the toolbar also exposes a help button so the
  // shortcut is discoverable.
  const [shortcutsModalOpen, setShortcutsModalOpen] = useState(false);
  useHotkeys(
    ['shift+/', '?'],
    event => {
      event.preventDefault();
      setShortcutsModalOpen(true);
    },
    { enableOnFormTags: false, enableOnContentEditable: false }
  );

  return (
    <CaptionHotkeyContext.Provider
      value={{
        shortcutsModalOpen,
        openShortcutsModal: () => setShortcutsModalOpen(true),
        closeShortcutsModal: () => setShortcutsModalOpen(false),
        searchOpen,
        openSearch: () => setSearchOpen(true),
        closeSearch: () => setSearchOpen(false)
      }}
    >
      {children}
    </CaptionHotkeyContext.Provider>
  );
}
